/**
 * Phase 2C.1/2C.2 (completion-session revision) — BPF Completion page.
 * Runs on /pages/complete-your-memorial ONLY (templates/page.complete-
 * your-memorial.json's own dedicated section, sections/bpf-completion.liquid).
 *
 * SECURITY: this page NEVER carries a raw completion credential in any
 * form — no URL fragment, no query parameter, no DOM attribute. The
 * credential exchange happens entirely on a separate, BPF-controlled
 * bootstrap host (complete.bestpetsforever.com/bootstrap, a plain
 * server page with zero third-party scripts — never Shopify-rendered)
 * BEFORE the customer ever lands here. By the time this script runs,
 * the only thing authorizing this browser is a Secure, HttpOnly
 * completion-session cookie already set on that other host — invisible
 * to this (or any) page's JavaScript, and never read/stored/transmitted
 * by this file directly at all. Every request below relies on
 * `credentials: 'include'` alone to carry it.
 *
 * The browser is NOT authoritative for anything about the memorial
 * itself (product/variant/properties/add-ons/price) — this page only
 * ever displays what the server already restored, and the ONLY things
 * a customer can supply here are the shipping address fields and an
 * explicit final confirmation, both re-validated fully server-side
 * before anything is created.
 *
 * content_for_header is intentionally UNCHANGED/preserved on this
 * page (see this phase's own report for why the session-cookie model
 * makes theme-level analytics suppression unnecessary) — Shopify Web
 * Pixels, Klaviyo, Clarity, and any other app embed may run normally
 * here; none of them ever has anything sensitive to observe.
 */
(function () {
  'use strict';

  var BPF_COMPLETION_CONTEXT_URL = 'https://complete.bestpetsforever.com/completion/context/v1';
  var BPF_COMPLETION_SUBMIT_URL = 'https://complete.bestpetsforever.com/completion/submit/v1';

  // Mirrors partner-production-order.ts's own SHIPPING_ALLOWED_COUNTRY_CODES
  // exactly — audited live against this shop's own shop.shipsToCountries
  // during Phase 2C.2 (see this phase's own final report, item C). United
  // States listed first (BPF's primary market), the rest alphabetical by
  // name.
  var SHIPPING_COUNTRIES = [
    ['US', 'United States'],
    ['AU', 'Australia'],
    ['AT', 'Austria'],
    ['BE', 'Belgium'],
    ['CA', 'Canada'],
    ['CZ', 'Czechia'],
    ['DK', 'Denmark'],
    ['FI', 'Finland'],
    ['FR', 'France'],
    ['DE', 'Germany'],
    ['HK', 'Hong Kong SAR'],
    ['IE', 'Ireland'],
    ['IL', 'Israel'],
    ['IT', 'Italy'],
    ['JP', 'Japan'],
    ['MY', 'Malaysia'],
    ['NL', 'Netherlands'],
    ['NZ', 'New Zealand'],
    ['NO', 'Norway'],
    ['PL', 'Poland'],
    ['PT', 'Portugal'],
    ['SG', 'Singapore'],
    ['KR', 'South Korea'],
    ['ES', 'Spain'],
    ['SE', 'Sweden'],
    ['CH', 'Switzerland'],
    ['AE', 'United Arab Emirates'],
    ['GB', 'United Kingdom'],
  ];

  function byId(id) {
    return document.getElementById(id);
  }

  function showState(name) {
    ['loading', 'error', 'content'].forEach(function (state) {
      var el = byId('bpf-completion-' + state);
      if (el) el.hidden = state !== name;
    });
  }

  function setErrorMessage(message) {
    var el = byId('bpf-completion-error-message');
    if (el) el.textContent = message;
  }

  function humanizeKey(key) {
    return String(key).replace(/[_-]/g, ' ').replace(/\b\w/g, function (c) {
      return c.toUpperCase();
    });
  }

  /**
   * Renders EVERYTHING the server returned, generically — never
   * hardcoding specific personalization field names, since Commerce
   * V2's own `properties[...]` field names are already human-readable
   * (e.g. "Pet Name", "Engraving", "Color") and may grow over time.
   */
  function renderContext(context) {
    var list = byId('bpf-completion-summary');
    if (!list) return;

    function addRow(label, value) {
      var row = document.createElement('div');
      row.className = 'bpf-completion__summary-row';
      var dt = document.createElement('span');
      dt.className = 'bpf-completion__summary-label';
      dt.textContent = label;
      var dd = document.createElement('span');
      dd.className = 'bpf-completion__summary-value';
      dd.textContent = value;
      row.appendChild(dt);
      row.appendChild(dd);
      list.appendChild(row);
    }

    if (context.animal) addRow('Animal', context.animal);
    if (context.breed) addRow('Breed', context.breed);
    if (context.memorialStyle) addRow('Memorial Style', context.memorialStyle);

    var properties = context.properties || {};
    Object.keys(properties).forEach(function (key) {
      var value = properties[key];
      if (value === null || value === undefined || value === '') return;
      addRow(humanizeKey(key), String(value));
    });

    var addons = context.addons || {};
    if (addons.pictureFrame && addons.pictureFrame.selected === true) {
      addRow('Picture Frame', 'Included');
      var frameColor = addons.pictureFrame.properties && addons.pictureFrame.properties.Color;
      if (frameColor) addRow('Frame Color', String(frameColor));
    }
  }

  /**
   * The authoritative live Shopify image the server already resolved
   * (variant image, else product featured image — see partner-
   * completion.ts's resolveProductImage()). Never a browser-supplied
   * URL. Falls back to a plain placeholder box (no broken-image icon)
   * when the server found neither.
   */
  function renderProductImage(context) {
    var img = byId('bpf-completion-product-image');
    var placeholder = byId('bpf-completion-product-placeholder');
    if (!img || !placeholder) return;

    if (context.productImageUrl) {
      img.src = context.productImageUrl;
      img.alt = context.productImageAlt || [context.breed, context.animal].filter(Boolean).join(' ') || 'Your memorial';
      img.hidden = false;
      placeholder.hidden = true;
    } else {
      img.hidden = true;
      placeholder.hidden = false;
    }
  }

  function populateCountrySelect() {
    var select = byId('bpf-completion-country');
    if (!select || select.options.length > 0) return;
    SHIPPING_COUNTRIES.forEach(function (entry) {
      var option = document.createElement('option');
      option.value = entry[0];
      option.textContent = entry[1];
      select.appendChild(option);
    });
  }

  /**
   * Renders the "already submitted" read-only state: hides the
   * shipping form entirely, shows a calm confirmation banner. Never
   * treated as an error — a completed order revisiting this page is a
   * normal, expected outcome, not a failure.
   */
  function renderCompleted() {
    var form = byId('bpf-completion-shipping-form');
    if (form) form.hidden = true;
    var banner = byId('bpf-completion-submitted');
    if (banner) banner.hidden = false;
  }

  function setSubmitError(message) {
    var el = byId('bpf-completion-submit-error');
    if (!el) return;
    if (message) {
      el.textContent = message;
      el.hidden = false;
    } else {
      el.textContent = '';
      el.hidden = true;
    }
  }

  /**
   * Phase 2C.2A correction — there is exactly ONE authoritative form
   * value for Address Line 1: the plain #bpf-completion-address1
   * input's own FormData entry, same as every other field. It is
   * ALWAYS kept in sync by assets/bpf-completion-address-
   * autocomplete.js (both on selecting a Google suggestion and, as a
   * backstop, right before this form's own submit button acts) — this
   * file has no special-case read of the autocomplete element at all
   * anymore, and needs none: if that script never loaded, was
   * blocked, or errored, the input was never hidden/touched and
   * FormData already reflects whatever the customer typed into it
   * directly, exactly as before the autocomplete enhancement existed.
   *
   * (A prior version of this file read the autocomplete element's own
   * .value directly at submit time instead. That left the CANONICAL
   * input empty+required+hidden after a suggestion was selected,
   * which live Chrome QA found silently blocks native form submission
   * entirely — see the autocomplete script's own header comment for
   * the full root-cause writeup.)
   */
  function collectShippingAddress(form) {
    var data = new FormData(form);
    var address = {
      first_name: (data.get('first_name') || '').toString().trim(),
      last_name: (data.get('last_name') || '').toString().trim(),
      address1: (data.get('address1') || '').toString().trim(),
      city: (data.get('city') || '').toString().trim(),
      postal_code: (data.get('postal_code') || '').toString().trim(),
      country_code: (data.get('country_code') || '').toString().trim(),
    };
    var address2 = (data.get('address2') || '').toString().trim();
    if (address2) address.address2 = address2;
    var province = (data.get('province') || '').toString().trim();
    if (province) address.province = province;
    var phone = (data.get('phone') || '').toString().trim();
    if (phone) address.phone = phone;
    return address;
  }

  /**
   * Wires the acknowledgement checkbox, the submit button's disabled
   * state, double-submit prevention, and the actual POST to
   * /completion/submit/v1. The SERVER remains authoritative for
   * idempotency — this only prevents an accidental duplicate BROWSER
   * request, never relied on as the real safety mechanism (see
   * partner-production-order.ts's own Firestore-backed lock).
   */
  function wireShippingForm() {
    var form = byId('bpf-completion-shipping-form');
    var acknowledge = byId('bpf-completion-acknowledge');
    var submitButton = byId('bpf-completion-submit');
    if (!form || !acknowledge || !submitButton) return;

    populateCountrySelect();
    submitButton.disabled = true; // never trust the markup's own `disabled` attribute alone — this must be true until the acknowledgement is explicitly checked

    var submitting = false;

    acknowledge.addEventListener('change', function () {
      submitButton.disabled = !acknowledge.checked || submitting;
    });

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      if (submitting || !acknowledge.checked) return;

      submitting = true;
      submitButton.disabled = true;
      submitButton.textContent = 'Submitting…';
      setSubmitError(null);

      var shippingAddress = collectShippingAddress(form);

      fetch(BPF_COMPLETION_SUBMIT_URL, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shipping_address: shippingAddress, confirmed: true }),
      })
        .then(function (response) {
          return response.json().then(function (data) {
            return { status: response.status, data: data };
          });
        })
        .then(function (result) {
          if (result.data && result.data.ok === true) {
            var banner = byId('bpf-completion-submitted');
            var message = byId('bpf-completion-submitted-message');
            if (message) {
              message.textContent = result.data.order_name
                ? 'Your memorial has been submitted for production. Reference: ' + result.data.order_name
                : 'Your memorial has been submitted for production.';
            }
            form.hidden = true;
            if (banner) banner.hidden = false;
            return;
          }

          var errorMessage = (result.data && result.data.error && result.data.error.message) || 'Something went wrong. Please try again.';
          if (result.data && result.data.error && result.data.error.code === 'completion_already_submitted') {
            renderCompleted();
            return;
          }
          setSubmitError(errorMessage);
          submitting = false;
          submitButton.disabled = !acknowledge.checked;
          submitButton.textContent = 'Submit Memorial for Production';
        })
        .catch(function () {
          setSubmitError('We could not submit your memorial right now. Please try again in a moment.');
          submitting = false;
          submitButton.disabled = !acknowledge.checked;
          submitButton.textContent = 'Submit Memorial for Production';
        });
    });
  }

  function init() {
    fetch(BPF_COMPLETION_CONTEXT_URL, {
      method: 'GET',
      credentials: 'include',
    })
      .then(function (response) {
        return response.json().then(function (data) {
          return { status: response.status, data: data };
        });
      })
      .then(function (result) {
        if (result.data && result.data.ok === true && result.data.context) {
          renderContext(result.data.context);
          renderProductImage(result.data.context);
          if (result.data.completed === true) {
            renderCompleted();
          } else {
            wireShippingForm();
          }
          showState('content');
        } else {
          var message = (result.data && result.data.error && result.data.error.message) || 'This completion link is invalid or has expired. Please use the original link from your email, or contact BPF support.';
          setErrorMessage(message);
          showState('error');
        }
      })
      .catch(function () {
        setErrorMessage('We could not load your memorial right now. Please try again in a moment.');
        showState('error');
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
